import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle2,
  Clock,
  Download,
  FileWarning,
  Inbox,
  XCircle,
} from 'lucide-react';

import {
  apiClient,
  type ApiError,
  type CaseListItem,
  type PortalUploadApproveDestination,
  type PortalUploadInboxItem,
  type PortalUploadStatusFilter,
} from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const PAGE_SIZE = 30;

const STATUS_FILTERS: { value: PortalUploadStatusFilter; label: string }[] = [
  { value: 'pending', label: 'Pendentes' },
  { value: 'approved', label: 'Aprovados' },
  { value: 'rejected', label: 'Recusados' },
  { value: 'all', label: 'Todos' },
];

const STATUS_BADGE: Record<
  PortalUploadInboxItem['status'],
  { label: string; cls: string; Icon: typeof Clock }
> = {
  pending: {
    label: 'Pendente',
    cls: 'bg-amber-500/15 text-amber-600 border-amber-500/30',
    Icon: Clock,
  },
  approved: {
    label: 'Aprovado',
    cls: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
    Icon: CheckCircle2,
  },
  rejected: {
    label: 'Recusado',
    cls: 'bg-red-500/15 text-red-600 border-red-500/30',
    Icon: XCircle,
  },
};

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDateTime(value: string): string {
  try {
    return new Date(value).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
}

/**
 * `/escritorio/portal/caixa-entrada` — caixa de entrada de upload do
 * Portal do Cliente, lado ESCRITORIO (docs/PORTAL-CLIENTE.md secao 6,
 * Marco 4, rodada 2). Rota EXATA: o link do template de notificacao
 * `portal.upload.received` (apps/api/.../notifications/templates/
 * portal-upload-received.ts) ja aponta para `/escritorio/portal/caixa-entrada`
 * — mudar o path aqui quebra o deep link do sino.
 */
export default function PortalCaixaEntradaPage() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<PortalUploadStatusFilter>('pending');
  const [page, setPage] = useState(1);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [approveTarget, setApproveTarget] = useState<PortalUploadInboxItem | null>(null);
  const [rejectTarget, setRejectTarget] = useState<PortalUploadInboxItem | null>(null);

  const query = useQuery({
    queryKey: ['portal-uploads', status, page],
    queryFn: () => apiClient.portalUploadsList({ status, page, pageSize: PAGE_SIZE }),
  });

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['portal-uploads'] });
  }

  async function handleDownload(item: PortalUploadInboxItem) {
    setDownloadError(null);
    setDownloadingId(item.id);
    try {
      const { blobUrl, filename } = await apiClient.portalUploadsDownloadBlob(item.id);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename ?? item.fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 2000);
    } catch (e) {
      setDownloadError((e as ApiError).message ?? 'Falha ao baixar o arquivo.');
    } finally {
      setDownloadingId(null);
    }
  }

  const items = query.data?.items ?? [];
  const totalPages = query.data?.totalPages ?? 1;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-lg font-semibold text-[var(--fg)]">
          Caixa de entrada do portal
        </h1>
        <p className="mt-1 text-sm text-[var(--fg-muted)]">
          Arquivos enviados pelos clientes pelo portal, aguardando revisão. Um
          arquivo só passa a valer no processo ou nos anexos do cliente depois
          de aprovado aqui.
        </p>
      </div>

      <div className="flex gap-1">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            onClick={() => {
              setStatus(f.value);
              setPage(1);
            }}
            className={cn(
              'rounded-md px-3 py-1.5 text-sm font-medium transition',
              'text-[var(--fg-muted)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] hover:text-[var(--fg)]',
              status === f.value &&
                'bg-[color-mix(in_oklch,var(--accent)_12%,transparent)] text-[var(--fg)]',
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      {downloadError && (
        <p className="rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-500">
          {downloadError}
        </p>
      )}

      <div className="overflow-hidden rounded-lg border border-[var(--border)] bg-[var(--surface)]">
        {query.isLoading ? (
          <p className="px-4 py-8 text-center text-sm text-[var(--fg-muted)]">
            Carregando...
          </p>
        ) : query.isError ? (
          <p className="px-4 py-8 text-center text-sm text-red-500">
            {(query.error as ApiError).message ?? 'Falha ao carregar a caixa de entrada.'}
          </p>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 px-4 py-12 text-center">
            <Inbox className="h-10 w-10 text-[var(--fg-muted)] opacity-40" />
            <p className="text-sm text-[var(--fg-muted)]">
              Nenhum arquivo {status === 'all' ? '' : STATUS_FILTERS.find((f) => f.value === status)?.label.toLowerCase()} por aqui.
            </p>
          </div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="border-b border-[var(--border)] text-xs text-[var(--fg-muted)]">
              <tr>
                <th className="px-4 py-2 font-medium">Cliente</th>
                <th className="px-4 py-2 font-medium">Arquivo</th>
                <th className="px-4 py-2 font-medium">Processo</th>
                <th className="px-4 py-2 font-medium">Tamanho</th>
                <th className="px-4 py-2 font-medium">Data</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const badge = STATUS_BADGE[item.status];
                return (
                  <tr key={item.id} className="border-b border-[var(--border)] last:border-0">
                    <td className="px-4 py-3 align-top">
                      <p className="font-medium text-[var(--fg)]">{item.clientName}</p>
                      {item.clientNote && (
                        <p className="mt-0.5 max-w-xs truncate text-xs text-[var(--fg-muted)]" title={item.clientNote}>
                          {item.clientNote}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top text-[var(--fg)]">{item.fileName}</td>
                    <td className="px-4 py-3 align-top text-[var(--fg-muted)]">
                      {item.caseNumeroCnj ?? '—'}
                    </td>
                    <td className="px-4 py-3 align-top text-[var(--fg-muted)]">
                      {formatBytes(item.sizeBytes)}
                    </td>
                    <td className="px-4 py-3 align-top text-[var(--fg-muted)]">
                      {formatDateTime(item.createdAt)}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <span
                        className={cn(
                          'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium',
                          badge.cls,
                        )}
                      >
                        <badge.Icon className="h-3 w-3" />
                        {badge.label}
                      </span>
                      {item.status === 'rejected' && item.rejectionReason && (
                        <p className="mt-1 max-w-[220px] text-[11px] text-[var(--fg-muted)]">
                          {item.rejectionReason}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="flex flex-wrap gap-1.5">
                        {item.status !== 'rejected' && (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={downloadingId === item.id}
                            onClick={() => void handleDownload(item)}
                          >
                            <Download className="mr-1 h-3.5 w-3.5" />
                            {downloadingId === item.id ? 'Baixando...' : 'Baixar'}
                          </Button>
                        )}
                        {item.status === 'pending' && (
                          <>
                            <Button size="sm" onClick={() => setApproveTarget(item)}>
                              Aprovar
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setRejectTarget(item)}
                            >
                              Recusar
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-[var(--fg-muted)]">
          <span>
            Página {query.data?.page ?? page} de {totalPages} · {query.data?.total ?? 0} no total
          </span>
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              Próxima
            </Button>
          </div>
        </div>
      )}

      {approveTarget && (
        <ApproveModal
          item={approveTarget}
          onClose={() => setApproveTarget(null)}
          onApproved={() => {
            setApproveTarget(null);
            invalidate();
          }}
        />
      )}

      {rejectTarget && (
        <RejectModal
          item={rejectTarget}
          onClose={() => setRejectTarget(null)}
          onRejected={() => {
            setRejectTarget(null);
            invalidate();
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal de aprovação
// ---------------------------------------------------------------------------

function ApproveModal({
  item,
  onClose,
  onApproved,
}: {
  item: PortalUploadInboxItem;
  onClose: () => void;
  onApproved: () => void;
}) {
  const [destination, setDestination] = useState<PortalUploadApproveDestination>(
    'client_attachment',
  );
  const [caseId, setCaseId] = useState<string>(item.caseId ?? '');
  const [caseSearch, setCaseSearch] = useState('');
  const [cases, setCases] = useState<CaseListItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const needsCasePicker = destination === 'case_attachment' && !item.caseId;

  useEffect(() => {
    if (!needsCasePicker) return;
    apiClient
      .casesList({ search: caseSearch || undefined, pageSize: 10 })
      .then((res) => setCases(res.items))
      .catch(() => setCases([]));
  }, [needsCasePicker, caseSearch]);

  async function handleConfirm() {
    setError(null);
    if (destination === 'case_attachment' && !caseId) {
      setError('Escolha o processo para vincular o arquivo.');
      return;
    }
    setSaving(true);
    try {
      await apiClient.portalUploadsApprove(item.id, {
        destination,
        caseId: destination === 'case_attachment' ? caseId : undefined,
      });
      onApproved();
    } catch (e) {
      setError((e as ApiError).message ?? 'Falha ao aprovar o upload.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-md rounded-xl border border-[var(--border)] bg-[var(--surface)] p-5 shadow-xl">
        <h3 className="text-sm font-semibold text-[var(--fg)]">
          Aprovar "{item.fileName}"
        </h3>
        <p className="mt-1 text-xs text-[var(--fg-muted)]">
          Enviado por {item.clientName}. Escolha para onde o arquivo vai.
        </p>

        <div className="mt-4 flex flex-col gap-2">
          <label className="flex items-start gap-2 rounded-md border border-[var(--border)] p-3 text-sm">
            <input
              type="radio"
              name="destination"
              className="mt-0.5"
              checked={destination === 'client_attachment'}
              onChange={() => setDestination('client_attachment')}
            />
            <span>
              <span className="block font-medium text-[var(--fg)]">
                Anexos do cliente
              </span>
              <span className="block text-xs text-[var(--fg-muted)]">
                Vai para a ficha de {item.clientName}, junto dos outros anexos.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 rounded-md border border-[var(--border)] p-3 text-sm">
            <input
              type="radio"
              name="destination"
              className="mt-0.5"
              checked={destination === 'case_attachment'}
              onChange={() => setDestination('case_attachment')}
            />
            <span>
              <span className="block font-medium text-[var(--fg)]">
                Vincular ao processo
              </span>
              <span className="block text-xs text-[var(--fg-muted)]">
                {item.caseNumeroCnj
                  ? `Processo ${item.caseNumeroCnj}. `
                  : ''}
                O vínculo fica registrado no histórico do envio.
              </span>
            </span>
          </label>
        </div>

        {needsCasePicker && (
          <div className="mt-3 rounded-md border border-[var(--border)] p-2">
            <input
              autoFocus
              value={caseSearch}
              onChange={(e) => setCaseSearch(e.target.value)}
              placeholder="Buscar processo por CNJ ou parte..."
              className="mb-2 h-8 w-full rounded-md border border-[var(--border)] bg-transparent px-2 text-xs focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
            />
            <ul className="max-h-40 space-y-1 overflow-y-auto">
              {cases.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => setCaseId(c.id)}
                    className={cn(
                      'w-full rounded-md px-2 py-1 text-left text-xs hover:bg-[color-mix(in_oklch,var(--fg)_6%,transparent)]',
                      caseId === c.id
                        ? 'bg-[color-mix(in_oklch,var(--accent)_12%,transparent)] text-[var(--fg)]'
                        : 'text-[var(--fg-muted)]',
                    )}
                  >
                    {c.numeroCnj ?? '(sem CNJ)'} — {c.mainPartyNames || c.actionTypeName || c.status}
                  </button>
                </li>
              ))}
              {cases.length === 0 && (
                <li className="px-2 py-1 text-xs text-[var(--fg-muted)]">
                  Nenhum processo encontrado.
                </li>
              )}
            </ul>
          </div>
        )}

        {error && <p className="mt-3 text-xs text-red-500">{error}</p>}

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button size="sm" onClick={handleConfirm} disabled={saving}>
            {saving ? 'Aprovando...' : 'Aprovar'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal de recusa
// ---------------------------------------------------------------------------

function RejectModal({
  item,
  onClose,
  onRejected,
}: {
  item: PortalUploadInboxItem;
  onClose: () => void;
  onRejected: () => void;
}) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm() {
    if (reason.trim().length < 3) {
      setError('Informe o motivo da recusa.');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await apiClient.portalUploadsReject(item.id, reason.trim());
      onRejected();
    } catch (e) {
      setError((e as ApiError).message ?? 'Falha ao recusar o upload.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-sm rounded-xl border border-[var(--border)] bg-[var(--surface)] p-5 shadow-xl">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-[var(--fg)]">
          <FileWarning className="h-4 w-4 text-red-500" />
          Recusar "{item.fileName}"
        </h3>
        <p className="mt-1 text-xs text-[var(--fg-muted)]">
          O arquivo será excluído do armazenamento. O cliente verá o motivo no
          histórico dele.
        </p>
        <textarea
          autoFocus
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          maxLength={1000}
          placeholder="Motivo da recusa..."
          className="mt-3 w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-xs text-[var(--fg)] placeholder:text-[var(--fg-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
        />
        {error && <p className="mt-2 text-xs text-red-500">{error}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="destructive" size="sm" onClick={handleConfirm} disabled={saving}>
            {saving ? 'Recusando...' : 'Recusar e excluir'}
          </Button>
        </div>
      </div>
    </div>
  );
}
